import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { ConfidenceBar } from '@/components/profiler/ConfidenceBar';
import type { DossierSectionProps } from './types';
import type { IdentifierVariant } from '@/types/profiler';

// ── helper: extract state-ish token from free text ────────────────────────────

const INDIA_STATES = [
  'Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'Telangana', 'Gujarat',
  'Rajasthan', 'Uttar Pradesh', 'West Bengal', 'Kerala', 'Punjab', 'Haryana',
  'Madhya Pradesh', 'Andhra Pradesh', 'Bihar', 'Odisha', 'Assam', 'Jharkhand',
  'Uttarakhand', 'Himachal Pradesh', 'Goa', 'Chhattisgarh', 'Manipur',
];

function extractState(address: string): string | null {
  for (const s of INDIA_STATES) {
    if (address.toLowerCase().includes(s.toLowerCase())) return s;
  }
  // try to extract last meaningful comma-separated token
  const parts = address.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length >= 2) return parts[parts.length - 1];
  return null;
}

// ── source chip ───────────────────────────────────────────────────────────────

function SourceChip({ source }: { source: string }) {
  return (
    <span
      style={{
        background: '#0C1118',
        color: '#5A7A9A',
        border: '1px solid #182030',
        fontSize: 8,
        fontFamily: 'monospace',
        padding: '2px 7px',
        borderRadius: 99,
      }}
    >
      {source}
    </span>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export function Addresses({ report, pipeline }: DossierSectionProps) {
  const cluster = pipeline?.cluster ?? report.identifier_cluster;
  const addressVariants: IdentifierVariant[] = cluster?.variants.filter(v => v.type === 'ADDRESS') ?? [];

  // Deduplicate
  const addresses = addressVariants.filter((v, i, arr) => arr.findIndex(x => x.value === v.value) === i);

  // Sort by confidence descending
  const sorted = [...addresses].sort((a, b) => b.confidence - a.confidence);

  // Extract states for distribution
  const stateMap: Record<string, number> = {};
  for (const a of sorted) {
    const state = extractState(a.value);
    if (state) {
      stateMap[state] = (stateMap[state] ?? 0) + 1;
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5"
    >
      {/* Address cards */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={12} style={{ color: '#F0B429' }} />
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em' }}>
            PHYSICAL ADDRESSES ({sorted.length})
          </p>
        </div>

        {sorted.length > 0 ? (
          <div className="flex flex-col gap-3">
            {sorted.map((v, i) => (
              <div
                key={i}
                style={{
                  background: '#0C1118',
                  border: `1px solid ${i < 2 ? '#00C8FF40' : '#182030'}`,
                  borderLeft: `3px solid ${i < 2 ? '#00C8FF' : '#F0B429'}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                }}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <p style={{ color: '#DCE8F5', fontSize: 12, lineHeight: 1.5, flex: 1 }}>
                    {v.value}
                  </p>
                  {i < 2 && (
                    <span
                      style={{
                        background: '#00C8FF18',
                        color: '#00C8FF',
                        border: '1px solid #00C8FF30',
                        fontSize: 8,
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        padding: '2px 6px',
                        borderRadius: 99,
                        flexShrink: 0,
                      }}
                    >
                      PRIMARY
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div style={{ flex: 1 }}>
                    <ConfidenceBar value={v.confidence} color={i < 2 ? '#00C8FF' : '#F0B429'} height={3} showLabel />
                  </div>
                  <SourceChip source={v.source} />
                </div>
                {extractState(v.value) && (
                  <div style={{ marginTop: 6 }}>
                    <span
                      style={{
                        background: '#F0B42918',
                        color: '#F0B429',
                        border: '1px solid #F0B42930',
                        fontSize: 8,
                        fontFamily: 'monospace',
                        padding: '1px 6px',
                        borderRadius: 99,
                      }}
                    >
                      {extractState(v.value)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '32px', textAlign: 'center' }}
          >
            <span style={{ color: '#283A50', fontSize: 10, fontFamily: 'monospace' }}>
              No address records found
            </span>
          </div>
        )}
      </div>

      {/* State distribution */}
      {Object.keys(stateMap).length > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            STATE / REGION DISTRIBUTION
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stateMap).map(([state, count]) => (
              <span
                key={state}
                style={{
                  background: '#0C1118',
                  color: '#DCE8F5',
                  border: '1px solid #182030',
                  fontSize: 10,
                  fontFamily: 'monospace',
                  padding: '4px 10px',
                  borderRadius: 6,
                }}
              >
                {state}
                {count > 1 && (
                  <span style={{ color: '#5A7A9A', marginLeft: 4 }}>×{count}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Geographic note */}
      <div
        style={{
          background: '#060E1A',
          border: '1px solid #1E2B3F',
          borderRadius: 8,
          padding: '10px 14px',
        }}
      >
        <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', lineHeight: 1.6 }}>
          Address records are sourced from identity registration databases, breach credential data, and
          OSINT cross-correlation. Confidence reflects source reliability and recency.
          Records marked PRIMARY indicate highest-confidence verified locations.
          Address data may include historical or transient residences.
        </p>
      </div>
    </motion.div>
  );
}
